/*
 * Copyright © 2026 Bruce Matin,  ALL RIGHTS RESERVED!
 */

import React, { useState } from 'react';
import { X, MapPin, Search, Navigation } from 'lucide-react';
import { useTheme } from '../theme';
import { getCurrentPosition } from '../utils/geolocation';

interface LocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (zip: string, radius: number, latitude?: number, longitude?: number) => void;
  initialZip?: string;
  initialRadius?: number;
  initialLat?: number;
  initialLng?: number;
}

const LocationModal: React.FC<LocationModalProps> = ({ 
  isOpen, 
  onClose, 
  onSave, 
  initialZip = '', 
  initialRadius = 10,
  initialLat,
  initialLng
}) => {
  const theme = useTheme();
  const [zip, setZip] = useState(initialZip);
  const [radius, setRadius] = useState(initialRadius);
  const [coords, setCoords] = useState<{lat?: number, lng?: number}>(
    { lat: initialLat, lng: initialLng }
  );
  const [isDetecting, setIsDetecting] = useState(false);

  if (!isOpen) return null;

  const radii = [1, 5, 10, 25, 50, 100];

  const handleDetectLocation = async () => {
    setIsDetecting(true);
    try {
      const position = await getCurrentPosition();
      setCoords({ lat: position.latitude, lng: position.longitude });
      setZip('Current Location'); // Visual feedback
    } catch (err) {
      console.error('Failed to get location:', err);
      alert('Could not detect location. Please check your browser permissions.');
    } finally {
      setIsDetecting(false);
    }
  };

  const handleZipChange = (newZip: string) => {
    setZip(newZip);
    // If user types, clear detected coords unless it matches "Current Location"
    if (newZip !== 'Current Location') {
      setCoords({});
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-sm" 
        onClick={onClose}
      />
      
      {/* Modal */}
      <div 
        className="relative w-full max-w-md rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200"
        style={{ backgroundColor: theme.colors.surface }}
      >
        <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: theme.colors.border }}>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-xl bg-blue-50 text-blue-600">
              <MapPin size={24} />
            </div>
            <h2 className="text-xl font-bold" style={{ color: theme.colors.text }}>Set Your Location</h2>
          </div>
          <button 
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            style={{ color: theme.colors.mutedText }}
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-bold mb-2" style={{ color: theme.colors.text }}>ZIP Code</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Search size={18} style={{ color: theme.colors.mutedText }} />
              </div>
              <input 
                type="text" 
                value={zip}
                onChange={(e) => handleZipChange(e.target.value)}
                placeholder="Enter ZIP code"
                className="w-full pl-11 pr-4 py-3 rounded-xl border focus:outline-none focus:ring-4 focus:ring-blue-500/10 transition-all"
                style={{ 
                  backgroundColor: theme.colors.background, 
                  borderColor: theme.colors.border,
                  color: theme.colors.text
                }}
              />
            </div>
          </div>

          <button
            onClick={handleDetectLocation}
            disabled={isDetecting}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold border transition-all hover:bg-gray-50"
            style={{ 
              borderColor: theme.colors.border,
              color: theme.colors.primary,
              backgroundColor: theme.colors.surface
            }}
          >
            {isDetecting ? (
              <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            ) : (
              <Navigation size={18} />
            )}
            Use My Current Location
          </button>

          <div>
            <label className="block text-sm font-bold mb-4" style={{ color: theme.colors.text }}>Radius</label>
            <div className="grid grid-cols-3 gap-3">
              {radii.map((r) => (
                <button
                  key={r}
                  onClick={() => setRadius(r)}
                  className={`py-2.5 rounded-xl font-bold border transition-all ${radius === r ? 'shadow-md scale-105' : 'hover:bg-gray-50'}`}
                  style={{ 
                    borderColor: radius === r ? theme.colors.primary : theme.colors.border,
                    backgroundColor: radius === r ? theme.colors.primary : theme.colors.surface,
                    color: radius === r ? 'white' : theme.colors.text
                  }}
                >
                  {r} mi
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="p-6 bg-gray-50 border-t flex flex-col gap-3" style={{ borderColor: theme.colors.border }}>
          <button 
            onClick={() => onSave(zip, radius, coords.lat, coords.lng)}
            className="w-full py-4 rounded-2xl font-bold text-white shadow-lg shadow-blue-500/20 hover:opacity-90 transition-all active:scale-[0.98]"
            style={{ backgroundColor: theme.colors.primary }}
          >
            Apply Changes
          </button>
          <button 
            onClick={onClose}
            className="w-full py-3 rounded-2xl font-bold transition-all hover:bg-gray-100"
            style={{ color: theme.colors.mutedText }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default LocationModal;
